import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MATERIAL_IMAGE_SIGNED_SECONDS, signMaterialImagePaths } from "../../helpers/materialImages";

const REFRESH_MS = Math.max(60, MATERIAL_IMAGE_SIGNED_SECONDS - 600) * 1000;

/**
 * Signed read URLs for the private material-images bucket, keyed by image_path.
 * Signs only what is needed, refreshes before expiry, and exposes `refresh`
 * so a replaced image (same stable path) gets a fresh URL immediately.
 */
export function useMaterialImageUrls(materials) {
  const [urls, setUrls] = useState({});
  const urlsRef = useRef(urls);
  urlsRef.current = urls;

  const paths = useMemo(
    () =>
      [...new Set((materials || []).map((m) => m?.image_path).filter(Boolean))].sort(),
    [materials],
  );
  const pathsKey = paths.join("|");

  const refresh = useCallback(async (list) => {
    const signed = (await signMaterialImagePaths(list)) || {};
    if (Object.keys(signed).length) {
      setUrls((previous) => ({ ...previous, ...signed }));
    }
    return signed;
  }, []);

  useEffect(() => {
    const missing = paths.filter((path) => !urlsRef.current[path]);
    if (missing.length) refresh(missing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey, refresh]);

  useEffect(() => {
    if (!paths.length) return undefined;
    const timer = window.setInterval(() => refresh(paths), REFRESH_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey, refresh]);

  const urlFor = useCallback(
    (material) => (material?.image_path ? urls[material.image_path] : undefined),
    [urls],
  );

  return { urlFor, refresh };
}
